// import "./AdminDashboard.css";
import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from 'axios';
import AdminDashboardLayout from "../../../Components/AdminDashboard/AdminDashboardLayout";
import Loading from 'react-fullscreen-loading';
import { toast } from "react-hot-toast";
import { Button, Modal, Form } from "react-bootstrap";
import DataTable from 'react-data-table-component';
import ShowTime from "../../../utils/ShowTime/ShowTime";
import MapProduct from "../../../utils/ProductMapper/MapProduct";
import countryList from 'react-select-country-list';
import Select from 'react-select'
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';
import CustomLoader from "../../../utils/DataTableLoader/CustomLoader";
import { Can, useAuth } from "../../../authentication/AuthContext";
import TruncatedText, { TruncatedTextTooltip, DateCell, COLUMN_WIDTHS } from "../../../Components/TruncatedText/TruncatedText";


// RequestQuote.request_type → chip. 'credits' rows are reseller credit
// purchases (Reseller → Buy Credits), not licence quotes.
const REQUEST_TYPES = {
  new: { label: 'New', bg: '#e0f2fe', color: '#075985' },
  extend: { label: 'Renewal', bg: '#ede9fe', color: '#5b21b6' },
  credits: { label: 'Credits', bg: '#fef3c7', color: '#92400e' },
};

export default function RequestQuotes() {

  const navigate = useNavigate();
  const { can } = useAuth();
  // Status is shown to everyone who can see the list; only users who may change
  // it get an editable control. Hiding it entirely would remove information they
  // are allowed to read.
  const canChangeStatus = can('admin.marketing.quote_status');
  const [TableData, setTableData] = useState([]);
  const [screenloader, setScreenloader] = useState(true);
  const [TotalRecords, setTotalRecords] = useState();
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState("id");
  const [sortOrder, setSortOrder] = useState("DESC");
  const [pending, setPending] = useState(true);

  const initialFilters = {
    'l.created_at': { operator: '', value: '' },
    email: { operator: '', value: '' },
    Product: { operator: '', value: '' },
    organization: { operator: '', value: '' },
    status: { operator: '', value: '' },
    request_type: { operator: '', value: '' },
    updated_at: { operator: '', value: '' },
  };
  const [filters, setFilters] = useState(initialFilters);
  const [createdAt, setCreatedAt] = useState("");
  const [updatedAt, setUpdatedAt] = useState("");
  const [product, setProduct] = useState("");
  const [email, setEmail] = useState("");
  const [organization, setOrganization] = useState("");
  const [country, setCountry] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [assigningId, setAssigningId] = useState(null);
  const CountriesList = useMemo(() => countryList().getData(), []);

  const [paymentLinkModal, setPaymentLinkModal] = useState({ show: false, quote: null, value: "", notify: true });
  const [savingPaymentLink, setSavingPaymentLink] = useState(false);

  const openPaymentLinkModal = (quote) => {
    setPaymentLinkModal({
      show: true,
      quote,
      value: quote.payment_link || "",
      notify: !quote.payment_link,
    });
  };

  const closePaymentLinkModal = () => {
    setPaymentLinkModal({ show: false, quote: null, value: "", notify: true });
  };

  const QUOTE_STATUS_OPTIONS = [
    { value: 'pending', label: 'Pending' },
    { value: 'payment_link_sent', label: 'Payment Link Sent' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' },
  ];

  const statusLabel = (value) =>
    QUOTE_STATUS_OPTIONS.find(o => o.value === value)?.label || value || 'Pending';

  const statusStyle = (value) => {
    const map = {
      pending: { bg: '#fef3c7', color: '#92400e' },
      payment_link_sent: { bg: '#dbeafe', color: '#1e40af' },
      completed: { bg: '#dcfce7', color: '#166534' },
      cancelled: { bg: '#fee2e2', color: '#991b1b' },
    };
    const c = map[value] || map.pending;
    return {
      backgroundColor: c.bg,
      color: c.color,
      padding: '2px 8px',
      borderRadius: 12,
      fontSize: '0.75rem',
      fontWeight: 600,
      whiteSpace: 'nowrap',
    };
  };

  const changeStatus = async (quote, newStatus) => {
    if (!quote || quote.status === newStatus) return;
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/admin/marketing/quotes/${quote.id}/status`,
        { status: newStatus },
        { headers: { 'access-token': localStorage.getItem('accessToken') } }
      );
      if (response.data.status) {
        setTableData(prev => prev.map(q => q.id === quote.id
          ? { ...q, status: response.data.quote_status, updated_at: new Date().toISOString() }
          : q));
        toast.success("Status updated");
      } else {
        toast.error(response.data.message || "Failed to update status");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update status");
    }
  };

  // Reseller credit requests: adds the request's credits to the reseller's
  // balance. The server stamps the quote so a second click (or a second admin)
  // is refused — the button disappearing here is a convenience, not the guard.
  const assignCredits = async (quote) => {
    setAssigningId(quote.id);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/admin/marketing/quotes/${quote.id}/assign_credits`,
        {},
        { headers: { 'access-token': localStorage.getItem('accessToken') } }
      );
      if (response.data.status) {
        setTableData(prev => prev.map(q => q.id === quote.id
          ? { ...q, credits_assigned_at: response.data.credits_assigned_at, status: response.data.quote_status, updated_at: new Date().toISOString() }
          : q));
        toast.success(response.data.message);
      } else {
        toast.error(response.data.message || "Could not assign credits");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Could not assign credits");
    } finally {
      setAssigningId(null);
    }
  };

  const confirmAssignCredits = (quote) => {
    const credits = Number(quote.credits).toLocaleString();
    const payLater = quote.payment_terms === 'pay_later';
    confirmAlert({
      title: `Assign ${credits} credits?`,
      message: payLater
        ? `Pay-later request: the credits land in ${quote.email}'s balance now and the invoice is due within 30 days. Mark the quote Completed once it is paid.`
        : `Prepaid request: only assign once payment has been received. The credits land in ${quote.email}'s balance and the quote is marked Completed.`,
      buttons: [
        { label: payLater ? 'Assign credits' : 'Payment received — assign', onClick: () => assignCredits(quote) },
        { label: 'Cancel' },
      ],
    });
  };

  const savePaymentLink = async () => {
    const { quote, value, notify } = paymentLinkModal;
    if (!quote) return;
    setSavingPaymentLink(true);
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/admin/marketing/quotes/${quote.id}/payment_link`,
        { payment_link: value, notify_customer: notify && !!value.trim() },
        { headers: { 'access-token': localStorage.getItem('accessToken') } }
      );
      if (response.data.status) {
        setTableData(prev => prev.map(q => q.id === quote.id ? { ...q, payment_link: response.data.payment_link } : q));
        toast.success(response.data.message || "Payment link saved");
        closePaymentLinkModal();
      } else {
        toast.error(response.data.message || "Failed to save payment link");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to save payment link");
    } finally {
      setSavingPaymentLink(false);
    }
  };

  const handleFiltersChange = (key, value, operator) => {
    setFilters(prevFilters => ({
      ...prevFilters,
      [key]: {
        operator: operator,
        value: value
      }
    }));
  };

  const handleFiltersReset = () => {
    setFilters(initialFilters);
    setCreatedAt("");
    setUpdatedAt("");
    setProduct("");
    setEmail("");
    setOrganization("");
    setCountry([]);
    setStatusFilter("");
    setTypeFilter("");
  }

  const handleEnterKeyPress = (e) => {
    var value = e.target.value;
    if (value !== "") {
      value = `%${value}%`
    }
    if (e.key === 'Enter') {
      handleFiltersChange(e.target.id, value, 'LIKE')
    }
  };

  const handleCopyEmailList = () => {
    const emails = TableData.map(license => license.email);
    const emailList = (emails.join(', '));
    try {
      navigator.clipboard.writeText(emailList);
      toast.success(`${emails.length} Emails copied to clipboard!`);
    } catch (error) {
      toast.error('Failed to copy email list to clipboard');
    }
  };
  const columns = [
    // { 
    //   name: "Actions", 
    //   sortable: false, 
    //   cell: row=>(<FaEdit
    //                 className="edit-icon"
    //                 onClick={() => {
    //                   navigate(`/Admin/Quotes/edit/${row.machine_id}`, {
    //                     replace: false,
    //                   });
    //                 }}
    //               />)
    // },
    {
      name: 'ID',
      selector: row => row.id,
    },
    {
      name: 'Type',
      selector: row => row.request_type,
      sortField: "request_type",
      sortable: true,
      minWidth: '110px',
      cell: row => {
        const type = REQUEST_TYPES[row.request_type] || REQUEST_TYPES.new;
        return (
          <span style={{
            backgroundColor: type.bg,
            color: type.color,
            padding: '2px 10px',
            borderRadius: 12,
            fontSize: '0.75rem',
            fontWeight: 600,
            whiteSpace: 'nowrap',
          }}>
            {type.label}
          </span>
        );
      },
    },
    {
      // Reseller credit requests only. Assigning is the fulfilment step: the
      // credits were fixed server-side when the reseller submitted.
      name: 'Credits',
      selector: row => row.credits,
      sortField: "credits",
      sortable: true,
      minWidth: '200px',
      cell: row => {
        if (row.request_type !== 'credits') return <span style={{ color: '#94a3b8' }}>—</span>;
        const payLater = row.payment_terms === 'pay_later';
        return (
          <div style={{ padding: '4px 0' }}>
            <div style={{ fontWeight: 700 }}>{Number(row.credits || 0).toLocaleString()} credits</div>
            <small style={{ color: payLater ? '#b45309' : '#64748b', fontWeight: payLater ? 600 : 400 }}>
              {payLater ? 'Pay later · 30 days' : 'Prepaid'}
            </small>
            <div className="mt-1">
              {row.credits_assigned_at ? (
                <small style={{ color: '#166534', fontWeight: 600 }}>✓ Assigned {ShowTime(row.credits_assigned_at)}</small>
              ) : row.status === 'cancelled' ? (
                <small style={{ color: '#94a3b8' }}>Cancelled</small>
              ) : (
                <Can permission="admin.marketing.quote_assign_credits">
                  <Button
                    size="sm"
                    variant="success"
                    disabled={assigningId === row.id}
                    onClick={() => confirmAssignCredits(row)}
                  >
                    {assigningId === row.id ? 'Assigning…' : 'Assign credits'}
                  </Button>
                </Can>
              )}
            </div>
          </div>
        );
      },
    },
    {
      // A renewal can now cover several licenses on one request, so this lists
      // all of them rather than only RequestQuote.license_id (which holds the
      // first of the set purely so the listing can still sort and filter on a
      // single scalar). `licenses` comes from RequestQuoteLicenses and is the
      // authority; the license_id fallback keeps rows created before that table
      // existed rendering exactly as they did.
      name: 'Licenses',
      sortable: false,
      minWidth: '240px',
      cell: row => {
        const attached = Array.isArray(row.licenses) && row.licenses.length
          ? row.licenses
          : (row.license_id
            ? [{
              id: row.license_id,
              license_reference_id: row.license_reference_id,
              expires_at: row.license_expires_at,
            }]
            : []);

        if (!attached.length) return <span style={{ color: '#94a3b8' }}>—</span>;

        const openLicense = (e, id) => {
          e.preventDefault();
          if (!can('admin.licenses.update')) return;
          navigate(`/Admin/Licenses/${id}/edit`);
        };

        return (
          <div style={{ whiteSpace: 'pre-wrap', padding: '4px 0' }}>
            {attached.length > 1 && (
              <div style={{ color: '#5b21b6', fontWeight: 600, fontSize: '0.75rem', marginBottom: 2 }}>
                {attached.length} licenses
              </div>
            )}
            {attached.map(l => (
              <div key={l.id}>
                <a
                  href={`/Admin/Licenses/${l.id}/edit`}
                  onClick={(e) => openLicense(e, l.id)}
                  style={{ fontWeight: 600 }}
                >
                  {l.license_reference_id || `#${l.id}`}
                </a>
                {l.expires_at && (
                  <small style={{ color: '#94a3b8' }}> · expires {ShowTime(l.expires_at)}</small>
                )}
              </div>
            ))}
          </div>
        );
      },
    },
    {
      name: 'Email',
      minWidth: `${COLUMN_WIDTHS.email}px`,
      selector: row => row.email,
      sortField: "email",
      sortable: true,
      cell: row => <TruncatedText text={row.email} maxWidth={COLUMN_WIDTHS.email} />,
    },
    {
      name: 'Product',
      minWidth: `${COLUMN_WIDTHS.product}px`,
      selector: row => row.Product,
      sortField: "Product",
      sortable: true,
      cell: row => (
        <TruncatedText text={MapProduct(row.Product, "simple")} maxWidth={COLUMN_WIDTHS.product}>
          {MapProduct(row.Product)}
        </TruncatedText>
      ),
    },
    {
      name: 'No of Licenses',
      selector: row => row.no_of_licenses,
      sortField: "no_of_licenses",
      sortable: true,
      cell: row => <div>{row.no_of_licenses ? row.no_of_licenses : "-"}</div>,
    },
    {
      name: 'Name',
      minWidth: `${COLUMN_WIDTHS.name}px`,
      selector: row => row.name,
      sortField: "name",
      sortable: true,
      cell: row => <TruncatedText text={row.name} maxWidth={COLUMN_WIDTHS.name} />,
    },
    {
      name: 'Phone',
      minWidth: `${COLUMN_WIDTHS.phone}px`,
      selector: row => row.phone,
      sortField: "phone",
      sortable: true,
      cell: row => <TruncatedText text={row.phone} maxWidth={COLUMN_WIDTHS.phone} />,
    },
    {
      name: 'Country',
      minWidth: `${COLUMN_WIDTHS.country}px`,
      selector: row => row.country,
      sortField: "country",
      sortable: true,
      cell: row => <TruncatedText text={row.country} maxWidth={COLUMN_WIDTHS.country} />,
    },
    {
      name: 'Industry',
      minWidth: `${COLUMN_WIDTHS.name}px`,
      selector: row => row.industry,
      sortField: "industry",
      sortable: true,
      cell: row => <TruncatedText text={row.industry} maxWidth={COLUMN_WIDTHS.name} />,
    },
    {
      name: 'Organization',
      minWidth: `${COLUMN_WIDTHS.organization}px`,
      selector: row => row.organization,
      sortField: "organization",
      sortable: true,
      cell: row => <TruncatedText text={row.organization} maxWidth={COLUMN_WIDTHS.organization} />,
    },
    {
      name: 'Plan',
      selector: row => row.plan,
      sortField: "plan",
      sortable: true,
      cell: row => <div>{row.plan ? row.plan : '-'}</div>,
    },
    {
      name: 'Message',
      minWidth: `${COLUMN_WIDTHS.notes}px`,
      selector: row => row.message,
      sortField: "message",
      sortable: true,
      cell: row => <TruncatedText text={row.message} maxWidth={COLUMN_WIDTHS.notes} />,
    },
    {
      name: 'Payment Link',
      sortable: false,
      minWidth: '220px',
      cell: row => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, padding: '6px 0' }}>
          {row.payment_link ? (
            <a href={row.payment_link} target="_blank" rel="noopener noreferrer" style={{ wordBreak: 'break-all' }}>
              {row.payment_link}
            </a>
          ) : (
            <span style={{ color: '#94a3b8' }}>No link set</span>
          )}
          <Can permission="admin.marketing.quote_payment_link">
            <Button size="sm" variant="outline-primary" onClick={() => openPaymentLinkModal(row)}>
              {row.payment_link ? 'Edit link' : 'Add link'}
            </Button>
          </Can>
        </div>
      ),
    },
    {
      name: 'Status',
      sortField: "status",
      sortable: true,
      minWidth: '180px',
      cell: row => {
        const s = statusStyle(row.status);
        if (!canChangeStatus) {
          return (
            <span style={{
              backgroundColor: s.backgroundColor,
              color: s.color,
              fontWeight: 600,
              fontSize: '0.8rem',
              borderRadius: 999,
              padding: '4px 12px',
              display: 'inline-block',
            }}>{statusLabel(row.status)}</span>
          );
        }
        return (
          <Form.Select
            size="sm"
            value={row.status || 'pending'}
            onChange={(e) => changeStatus(row, e.target.value)}
            style={{
              backgroundColor: s.backgroundColor,
              color: s.color,
              fontWeight: 600,
              fontSize: '0.8rem',
              borderRadius: 999,
              borderColor: s.backgroundColor,
              paddingTop: 4,
              paddingBottom: 4,
              width: 'auto',
              minWidth: 160,
            }}
          >
            {QUOTE_STATUS_OPTIONS.map(o => (
              <option key={o.value} value={o.value} style={{ backgroundColor: '#ffffff', color: '#0f172a' }}>
                {o.label}
              </option>
            ))}
          </Form.Select>
        );
      },
    },
    {
      name: 'Updated At',
      minWidth: `${COLUMN_WIDTHS.date}px`,
      selector: row => row.updated_at,
      sortField: "updated_at",
      sortable: true,
      cell: row => <DateCell value={row.updated_at ? ShowTime(row.updated_at) : null} />,
    },
    {
      name: 'Created At',
      minWidth: `${COLUMN_WIDTHS.date}px`,
      selector: row => row.created_at,
      sortField: "created_at",
      sortable: true,
      cell: row => <DateCell value={ShowTime(row.created_at)} />,
    },

  ];

  const getUsersList = async () => {
    // console.log(page,pageSize,sortBy,sortOrder,filters)
    try {
      const formattedFilters = Object.keys(filters)
        .filter(key => filters[key].value) // Filter out falsy values
        .map((key, index) => ({
          [`filters[${index}][column]`]: key,
          [`filters[${index}][operator]`]: filters[key].operator,
          [`filters[${index}][value]`]: filters[key].value,
        }))
        .reduce((acc, obj) => ({ ...acc, ...obj }), {});

      const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/marketing/quotes`, {
        params: {
          limit: pageSize,
          page: page,
          sort_by: sortBy,
          sort_order: sortOrder,
          ...formattedFilters
        },
        headers: {
          'access-token': localStorage.getItem('accessToken'),
        },
      });
      setScreenloader(false);
      setPending(false);
      if (response.data.status) {
        const total = response.data.meta.pagination.total;
        setTotalRecords(total);
        setTableData(response.data.quotes);
      } else {
        toast.error('Something Went Wrong');
        console.log(response.data);
      }
    } catch (error) {
      setScreenloader(false);
      setPending(false);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        console.error(error);
        toast.error('Something Went Wrong');
      }
    }
  };
  useEffect(() => {
    setPending(true);
    getUsersList();
  }, [filters, page, pageSize, sortBy, sortOrder]);

  const now = new Date();

  const startOfDay = new Date(now);
  startOfDay.setHours(0, 0, 0, 0);

  const startOf3DaysAgo = new Date(now);
  startOf3DaysAgo.setDate(startOf3DaysAgo.getDate() - 2);
  startOf3DaysAgo.setHours(0, 0, 0, 0);

  const endOf3DaysAfter = new Date(now);
  endOf3DaysAfter.setDate(endOf3DaysAfter.getDate() + 3);
  endOf3DaysAfter.setHours(0, 0, 0, 0);

  const startOf1WeekAgo = new Date(now);
  startOf1WeekAgo.setDate(startOf1WeekAgo.getDate() - 7);
  startOf1WeekAgo.setHours(0, 0, 0, 0);

  const endOf1WeekAfter = new Date(now);
  endOf1WeekAfter.setDate(endOf1WeekAfter.getDate() + 7);
  endOf1WeekAfter.setHours(0, 0, 0, 0);

  const startOf1MonthAgo = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
  startOf1MonthAgo.setHours(0, 0, 0, 0);

  const endOf1MonthAfter = new Date(now.getFullYear(), now.getMonth() + 1, now.getDate());
  endOf1MonthAfter.setHours(0, 0, 0, 0);

  const startOf2MonthAgo = new Date(now.getFullYear(), now.getMonth() - 2, now.getDate());
  startOf2MonthAgo.setHours(0, 0, 0, 0);

  const endOf2MonthAfter = new Date(now.getFullYear(), now.getMonth() + 2, now.getDate());
  endOf2MonthAfter.setHours(0, 0, 0, 0);


  return (
    <div>
      <AdminDashboardLayout />
      <Loading loading={screenloader} background="#fff" loaderColor="#883484" />
      <div className='content-page-bg'>
        <div className='content-page'>
          <div className='content'>

            <div className="row">
              <h2>Requested Quotes</h2>
            </div>

            <div className="filters row">
              <div className="col-md-3 mb-3">
                <div className="input-group">
                  <input
                    type="email"
                    className="form-control"
                    id="email"
                    placeholder='Email'
                    value={email}
                    onChange={e => { setEmail(e.target.value); }}
                    onKeyDown={handleEnterKeyPress}
                  />
                  {email && (
                    <span
                      className="input-group-text"
                      role="button"
                      onClick={() => handleEnterKeyPress({ key: 'Enter', target: { id: 'email', value: email } })}
                    >
                      Apply
                    </span>
                  )}
                </div>
              </div>

              <div className="col-md-3 mb-3">
                <div className="input-group">
                  <input
                    type="text"
                    className="form-control"
                    id="organization"
                    placeholder='Organization'
                    value={organization}
                    onChange={e => { setOrganization(e.target.value); }}
                    onKeyDown={handleEnterKeyPress}
                  />
                  {organization && (
                    <span
                      className="input-group-text"
                      role="button"
                      onClick={() => handleEnterKeyPress({ key: 'Enter', target: { id: 'organization', value: organization } })}
                    >
                      Apply
                    </span>
                  )}
                </div>
              </div>


              <div className="col-md-3 mb-3">
                <Select placeholder="Country" options={CountriesList} value={country} onChange={(e) => { setCountry(e); handleFiltersChange('country', e.label, '=') }} />
              </div>

              <div className="col-md-3 mb-3">
                <Select
                  placeholder="Updated At"
                  options={[
                    { value: "", label: "All" },
                    { value: startOfDay.toISOString(), label: "Today" },
                    { value: startOf3DaysAgo.toISOString(), label: "Past 3 Days" },
                    { value: startOf1WeekAgo.toISOString(), label: "Within a Week" },
                    { value: startOf1MonthAgo.toISOString(), label: "Within a Month" },
                    { value: startOf2MonthAgo.toISOString(), label: "Within 2 Months" },
                  ]}
                  value={updatedAt}
                  onChange={(e) => { setUpdatedAt(e); handleFiltersChange('updated_at', e.value, '>=') }}
                />
              </div>

              <div className="col-md-3 mb-3">
                <Select
                  placeholder="Created At"
                  options={[
                    { value: "", label: "All" },
                    { value: startOfDay.toISOString(), label: "Today" },
                    { value: startOf3DaysAgo.toISOString(), label: "Past 3 Days" },
                    { value: startOf1WeekAgo.toISOString(), label: "Within a Week" },
                    { value: startOf1MonthAgo.toISOString(), label: "Witin a Month" },
                    { value: startOf2MonthAgo.toISOString(), label: "Within 2 Months" },
                  ]}
                  value={createdAt}
                  onChange={(e) => { setCreatedAt(e); handleFiltersChange('created_at', e.value, '>=') }}
                />
              </div>

              <div className="col-md-3 mb-3">
                <Select
                  placeholder="Product"
                  options={[
                    { value: "", label: "All" },
                    { value: "SPCAD_ACAD_Beta", label: "AUTOCAD" },
                    { value: "SPCAD_CADMATE", label: "CADMATE" },
                    { value: "SPCAD_GSTARCAD", label: "GSTARCAD" },
                    { value: "SPCAD_BRX", label: "BRICSCAD" },
                    { value: "SPCAD_ZWCAD", label: "ZWCAD" },
                  ]}
                  value={product}
                  onChange={(e) => { setProduct(e); handleFiltersChange('Product', e.value, '=') }}
                />
              </div>

              <div className="col-md-3 mb-3">
                <Select
                  placeholder="Status"
                  options={[
                    { value: "", label: "All" },
                    ...QUOTE_STATUS_OPTIONS,
                  ]}
                  value={statusFilter}
                  onChange={(e) => { setStatusFilter(e); handleFiltersChange('status', e.value, '=') }}
                />
              </div>

              <div className="col-md-3 mb-3">
                <Select
                  placeholder="Type"
                  options={[
                    { value: "", label: "All" },
                    ...Object.entries(REQUEST_TYPES).map(([value, t]) => ({ value, label: t.label })),
                  ]}
                  value={typeFilter}
                  onChange={(e) => { setTypeFilter(e); handleFiltersChange('request_type', e.value, '=') }}
                />
              </div>

            </div>

            <div className="row">
              <div className="col-6 col-md-6 mb-3 d-flex">
                <Button onClick={handleCopyEmailList} variant="primary" className="admin-btn">
                  Copy Email List
                </Button>
              </div>
              <div className="col-6 col-md-6 mb-3 d-flex justify-content-end align-items-end">
                <Button onClick={handleFiltersReset} variant="primary" className="admin-btn">
                  Clear all Filters
                </Button>
              </div>
            </div>
            <div className="row d-flex d-sm-none">
              <p>Total Records: <strong>{TotalRecords}</strong></p>
            </div>

            <div className="table-wrapper">
              <DataTable
                //data
                columns={columns}
                data={TableData}
                //style
                dense
                direction="auto"
                highlightOnHover
                noHeader
                persistTableHead
                pointerOnHover
                responsive
                subHeaderAlign="right"
                subHeaderWrap
                progressPending={pending}
                progressComponent={<CustomLoader />}
                // conditionalRowStyles={conditionalRowStyles}
                //pagination
                pagination
                paginationServer
                paginationPerPage={pageSize}
                paginationTotalRows={TotalRecords}
                paginationRowsPerPageOptions={[10, 20, 50, 100, 150]}
                onChangePage={(page) => setPage(page)}
                onChangeRowsPerPage={(currentRowsPerPage, currentPage) => { setPageSize(currentRowsPerPage); setPage(currentPage) }}
                sortServer
                onSort={(column, sortDirection) => { setSortBy(column.sortField); setSortOrder(sortDirection); }}
              />
            </div>
          </div>
        </div>
      </div>

      <Modal show={paymentLinkModal.show} onHide={closePaymentLinkModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Payment Link</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {paymentLinkModal.quote && (
            <p className="mb-2" style={{ color: '#64748b' }}>
              Quote <strong>#{paymentLinkModal.quote.id}</strong> &middot; {paymentLinkModal.quote.email}
            </p>
          )}
          <Form.Group>
            <Form.Label>Payment URL</Form.Label>
            <Form.Control
              type="url"
              placeholder="https://..."
              value={paymentLinkModal.value}
              onChange={(e) => setPaymentLinkModal(prev => ({ ...prev, value: e.target.value }))}
            />
            <Form.Text style={{ color: '#94a3b8' }}>
              Leave blank to remove the link.
            </Form.Text>
          </Form.Group>

          <Form.Group className="mt-3">
            <Form.Check
              type="switch"
              id="notify-customer-switch"
              label="Email customer that their payment link is ready"
              checked={paymentLinkModal.notify}
              disabled={!paymentLinkModal.value.trim()}
              onChange={(e) => setPaymentLinkModal(prev => ({ ...prev, notify: e.target.checked }))}
            />
            <Form.Text style={{ color: '#94a3b8' }}>
              {paymentLinkModal.value.trim()
                ? 'A notification email will be sent when you save.'
                : 'Add a payment URL above to enable customer notifications.'}
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closePaymentLinkModal} disabled={savingPaymentLink}>
            Cancel
          </Button>
          <Button variant="primary" onClick={savePaymentLink} disabled={savingPaymentLink}>
            {savingPaymentLink ? 'Saving…' : 'Save'}
          </Button>
        </Modal.Footer>
      </Modal>
      <TruncatedTextTooltip />
    </div>
  );
}
